import { Component, ContentChild, ElementRef, EventEmitter, Inject, Input, OnChanges, OnInit, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';

import "core-js";
import { BehaviorSubject, combineLatest, interval, Observable, of, ReplaySubject, merge, Subject, empty, NEVER, fromEvent } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, startWith, switchMap, tap } from 'rxjs/operators';
import { MatPaginator } from '@angular/material/paginator';
import { v4 as uuidv4 } from 'uuid';

import { Injectable } from '@angular/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Color } from '@swimlane/ngx-charts';
import { GraphQlQuery, GraphQlResultsSeriesOptions, IGraphQlService } from '../../../services/GraphQl/IGraphQL.service';


@Component({
  selector: 'graphql-stacked-bar-chart-pagination',
  templateUrl: './stackedbarchartpagination.component.html',
  styleUrls: ['./stackedbarchartpagination.component.css']
})
export class GraphQlStackedBarChartPaginationComponent implements OnInit, OnChanges {


  @Input()
  query: GraphQlQuery = null;
  queryObserable: ReplaySubject<GraphQlQuery> = new ReplaySubject<GraphQlQuery>(1);

  @Input()
  graphQlResultsSeriesOptions: GraphQlResultsSeriesOptions = null;

  @Input()
  options: StackedBarChartOptions = null;

  @Output()
  select: EventEmitter<any> = new EventEmitter();

  @ContentChild('eveTooltipTemplate') eveTooltipTemplate: TemplateRef<any>;
  @ViewChild(MatPaginator, { static: false }) set paginator(paginator: MatPaginator) { this.paginatorObserable.next(paginator) };
  paginatorObserable: BehaviorSubject<MatPaginator> = new BehaviorSubject(null);


  parentElement: any = null;

  scheme = {
    domain: ['#00abca', '#be0078', '#85bf75', '#c23133']
  };
  gradient: boolean = true;
  legend: boolean = true;
  legendPosition: string = 'right';
  legendTitle: string = "";
  labels: boolean = true;
  doughnut: boolean = true;
  showXAxis = true;
  showYAxis = true;
  showLegend = true;
  showXAxisLabel = true;
  showYAxisLabel = true;
  animations = true

  excludeResults: BehaviorSubject<String[]> = new BehaviorSubject([]);





  resultsObserable = fromEvent(document, "visibilitychange").pipe(
    startWith(null),
    switchMap(() => document.visibilityState === "hidden" ? empty() :
      combineLatest(
        combineLatest(
          this.queryObserable.pipe(
            tap(() => { this.paginatorObserable?.value?.firstPage() }),
            switchMap(query => {
              const refreshSubject = new Subject<number>();
              return merge(of(0), refreshSubject).pipe(map(() => ({ query, refreshSubject })));
            }),
            switchMap(({ query, refreshSubject }) => this.graphQlService.getGraphQlResultsSeries(query, this.graphQlResultsSeriesOptions).pipe(
              catchError(val => {
                return NEVER;
              }),
              map(result => ({ query, refreshSubject, result }))
            )),
            tap(({ refreshSubject }) => setTimeout(() => refreshSubject.next(0), 5000)),
            map(({ result }) => result)
          ),
          this.excludeResults
        ).pipe(
          map(([results, excludeResults]) => results.map(r => Object.assign({}, r, { series: r.series.map(s => excludeResults.indexOf(s.name) == -1 ? s : Object.assign({}, s, { value: 0 })) }))),
        ),
        this.paginatorObserable.pipe(
          switchMap(paginator => paginator?.page ?? of({ pageIndex: 0, pageSize: 10 }))
        )
      ).pipe(
        map(([results, paginator]) => {

          const _paginator = <any>paginator;

          return ({
            results,
            resultsPage: results.slice(_paginator.pageIndex * _paginator.pageSize, (_paginator.pageIndex + 1) * _paginator.pageSize)
          });
        })
      )
    )
  )



  parentWidth: Observable<number> = interval(10).pipe(
    map(() => this.parentElement.offsetWidth),
    distinctUntilChanged()
  );

  parentHeight: Observable<number> = interval(10).pipe(
    map(() => this.parentElement.offsetHeight),
    distinctUntilChanged()
  );

  view: Observable<number[]> = combineLatest(this.parentWidth, this.parentHeight).pipe(
    map(a => [a[0], a[1] - 135]),
    debounceTime(15)
  );



  legendStyles: Observable<SafeHtml> = this.excludeResults.pipe(
    map(excludeResult => {
      return this.sanitizer.bypassSecurityTrustHtml(excludeResult.map((e, p) => `<style>
      .${this.componenetId} ngx-charts-legend-entry [title="${e}"] {
        opacity: 0.2;
      }
    </style>`
      ).join("\r\n"))
    })
  );

  componenetId: String = "componenetId_" + uuidv4().replaceAll("-", "");

  constructor(
    @Inject("BASE_REPORTING") public reportingUrl: string,
    public graphQlService: IGraphQlService,
    private elRef: ElementRef,
    private sanitizer: DomSanitizer
  ) {

  }


  ngOnInit() {

    this.elRef.nativeElement.classList.add(this.componenetId);

    this.parentElement = this.elRef.nativeElement.parentElement;
    this.queryObserable.next(this.query);




  }




  ngOnChanges(changes: SimpleChanges): void {
    if (changes["query"] != null) this.queryObserable.next(this.query);
  }


  onSelect(data): void {
    if (!this.isLegend(data)) {
      this.select.emit({ result: data, query: this.query });
    }
    else {
      const excludeResults = this.excludeResults.getValue();



      if (excludeResults.indexOf(data) == -1) this.excludeResults.next(excludeResults.concat([data]));
      else this.excludeResults.next(excludeResults.filter(i => i != data));
    }
  }
  isLegend = (event) => typeof event === 'string';





}

@Injectable()
export class MatPaginatorIntlLabels extends MatPaginatorIntl {
  override itemsPerPageLabel = 'Users per page:';
}

export interface StackedBarChartOptions {
  width: number,
  height: number,
  chartHeader: string,
  xAxisLabel: string,
  yAxisLabel: string,
  colourScheme: string | Color
}
